import { useCallback, useEffect, useRef, useState } from "react";
import {
  coachClans,
  coachCheck,
  coachRowing,
  getLeaderboard,
  type CoachClan,
  type LeaderboardRow,
  type StaffRole,
} from "../lib/api";
import { useStaffRole } from "../components/staffRoleContext";
import { clearStaffCode } from "../lib/api";
import StoryReview from "../components/StoryReview";
import RefreshButton from "../components/RefreshButton";
import LangPills from "../components/LangPills";
import PhoneInput from "../components/PhoneInput";
import { normalizePhone } from "../lib/phone";
import { useT } from "../i18n";
import { errorMessage } from "../i18n/errors";

function TaskCheckbox({
  done,
  busy,
  title,
  checkable,
  ownerLabel,
  onToggle,
}: {
  done: boolean;
  busy: boolean;
  title: string;
  checkable: boolean;
  ownerLabel?: string;
  onToggle: () => void;
}) {
  const circle = (
    <span
      className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors ${
        done ? "bg-success" : "border-2 border-white/15"
      }`}
    >
      {done && (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );

  // Tasks confirmed by another role are shown read-only with an owner badge.
  if (!checkable) {
    return (
      <div className="w-full flex items-center gap-3 p-3 rounded-[12px] opacity-60">
        {circle}
        <span className={`flex-1 text-base ${done ? "text-muted line-through" : "text-white/80"}`}>{title}</span>
        {ownerLabel && (
          <span className="text-[11px] text-muted shrink-0 rounded-full bg-white/[0.06] px-2 py-1">{ownerLabel}</span>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      aria-pressed={done}
      className="w-full flex items-center gap-3 p-3 rounded-[12px] text-left active:bg-white/5 transition-colors disabled:opacity-50"
    >
      {circle}
      <span className={`text-base ${done ? "text-muted line-through" : "text-white"}`}>{title}</span>
    </button>
  );
}

function ClanCard({
  clan,
  busyKey,
  role,
  onToggle,
}: {
  clan: CoachClan;
  busyKey: string | null;
  role: StaffRole | null;
  onToggle: (taskKey: string, done: boolean) => void;
}) {
  const { t } = useT();
  return (
    <div className="bg-card border border-line-2 rounded-[18px] p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        {clan.emoji && <span className="text-3xl leading-none">{clan.emoji}</span>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-extrabold leading-tight">{clan.name}</h2>
            <span className="text-xs font-medium text-violet-lighter bg-violet-t14 rounded-full px-2.5 py-1">
              {clan.code}
            </span>
            {clan.completed && (
              <span className="text-xs font-semibold text-white bg-success rounded-full px-2.5 py-1">{t("coach.prizeGranted")}</span>
            )}
            {!clan.completed && clan.ready && (
              <span className="text-xs font-semibold text-gold bg-gold/10 rounded-full px-2.5 py-1">{t("coach.ready")}</span>
            )}
          </div>
          <p className="text-sm text-muted mt-1">
            {t("coach.leader", { name: clan.leader?.nickname || "—" })}
            {clan.leader?.phone ? ` · ${clan.leader.phone}` : ""}
          </p>
          <p className="text-sm text-muted">
            {t("coach.members", {
              count: clan.memberCount,
              done: clan.tasksDone,
              total: clan.tasksTotal,
            })}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        {clan.tasks.map((task) => {
          const checkable = role === "admin" || (!!task.role && task.role === role);
          return (
            <TaskCheckbox
              key={task.key}
              done={task.done}
              busy={busyKey === `${clan.id}:${task.key}`}
              title={task.title}
              checkable={checkable}
              ownerLabel={!checkable && task.role ? t(`staff.role.${task.role}`) : undefined}
              onToggle={() => onToggle(task.key, !task.done)}
            />
          );
        })}
        {clan.tasks.length === 0 && <p className="text-sm text-muted p-3">{t("coach.noTasks")}</p>}
      </div>
    </div>
  );
}

function ClansTab({ role }: { role: StaffRole | null }) {
  const { t } = useT();
  const [query, setQuery] = useState("");
  const [clans, setClans] = useState<CoachClan[]>([]);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(
    async (q: string) => {
      try {
        const r = await coachClans(q.trim() || undefined);
        setClans(r.clans);
        setError("");
      } catch (e: any) {
        setError(errorMessage(e, t, "coach.errLoadClans"));
      }
    },
    [t],
  );

  // Дебаунс поиска: при изменении query через 350 мс перезапрашиваем список.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => refresh(query), 350);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, refresh]);

  async function toggle(clanId: string, taskKey: string, done: boolean) {
    setBusyKey(`${clanId}:${taskKey}`);
    try {
      await coachCheck(clanId, taskKey, done);
      await refresh(query);
    } catch (e: any) {
      setError(errorMessage(e, t, "coach.errCheck"));
    } finally {
      setBusyKey(null);
    }
  }

  // Задание моей роли: сводка «отмечено у X из Y» и сортировка «неотмеченные сверху»
  // (админ видит всё как есть).
  const myTaskDone = (clan: CoachClan) => {
    const task = clan.tasks.find((task) => task.role === role);
    return task ? task.done : true; // у клана нет моего задания — не поднимаем его вверх
  };
  const roleScoped = role && role !== "admin";
  const withMyTask = roleScoped ? clans.filter((c) => c.tasks.some((task) => task.role === role)) : [];
  const myDoneCount = withMyTask.filter(myTaskDone).length;
  const sortedClans = roleScoped ? [...clans].sort((a, b) => Number(myTaskDone(a)) - Number(myTaskDone(b))) : clans;

  return (
    <>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("coach.searchPlaceholder")}
        className="w-full bg-input text-black rounded-[14px] px-4 py-4 text-base placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
      />
      {roleScoped && withMyTask.length > 0 && (
        <p className="text-sm font-semibold text-violet-lighter mt-3">
          {t("coach.myTaskProgress", { done: myDoneCount, total: withMyTask.length })}
        </p>
      )}
      {error && <p className="text-sm text-red-400 mt-3">{error}</p>}

      {clans.length === 0 ? (
        <p className="text-muted text-base text-center py-12">
          {query ? t("coach.nothingFound") : t("coach.noClans")}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {sortedClans.map((clan) => (
            <ClanCard
              key={clan.id}
              clan={clan}
              busyKey={busyKey}
              role={role}
              onToggle={(taskKey, done) => toggle(clan.id, taskKey, done)}
            />
          ))}
        </div>
      )}
    </>
  );
}

// Лидерборд «только чтение» для RedBull: они награждают победителей,
// поэтому видят топ-5 каждого зачёта, но не вносят результаты.
function BoardTab() {
  const { t } = useT();
  const [board, setBoard] = useState<LeaderboardRow[]>([]);
  const [error, setError] = useState("");

  const load = useCallback(
    () =>
      getLeaderboard()
        .then((r) => {
          setBoard(r.leaderboard);
          setError("");
        })
        .catch(() => setError(t("coach.rowing.errBoard"))),
    [t],
  );
  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const MEDAL = ["🥇", "🥈", "🥉"];
  const Top5 = ({ sx }: { sx: "F" | "M" }) => {
    const rows = board.filter((r) => r.sex === sx).slice(0, 5);
    return (
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-extrabold">
          {sx === "F" ? t("prizes.rowing.women") : t("prizes.rowing.men")}
        </h2>
        <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
          {rows.map((row, i) => (
            <div key={`${row.nickname}-${i}`} className="flex items-center gap-3 p-4">
              <span className="w-8 shrink-0 text-lg">{MEDAL[i] ?? <span className="text-muted text-sm font-semibold">{i + 1}</span>}</span>
              <span className="flex-1 text-sm font-semibold truncate">{row.nickname || t("coach.rowing.guestFallback")}</span>
              <span className="text-sm font-bold">{Math.round(row.power_w)} {t("unit.watts")}</span>
              <span className="text-muted text-sm w-16 text-right">{row.distance_m} {t("unit.meters")}</span>
            </div>
          ))}
          {rows.length === 0 && <div className="p-4 text-muted text-sm">{t("coach.rowing.boardEmpty")}</div>}
        </div>
      </section>
    );
  };

  return (
    <div className="flex flex-col gap-6 max-w-md">
      <div className="flex items-center justify-between gap-3 -mb-2">
        <p className="text-muted text-sm">{t("coach.board.hint")}</p>
        <RefreshButton onRefresh={load} label={t("admin.refresh")} />
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <Top5 sx="F" />
      <Top5 sx="M" />
    </div>
  );
}

function RowingTab() {
  const { t } = useT();
  // Достаточно одного из двух: ник ИЛИ телефон.
  const [nick, setNick] = useState("");
  const [phone, setPhone] = useState("");
  const [distance, setDistance] = useState("");
  const [result, setResult] = useState<{ distance_m: number; power_w: number } | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [board, setBoard] = useState<LeaderboardRow[]>([]);
  const [boardError, setBoardError] = useState("");
  // Зачёты раздельные — тренер смотрит лидерборд по полу.
  const [boardSex, setBoardSex] = useState<"F" | "M">("F");

  const loadBoard = useCallback(async () => {
    try {
      const r = await getLeaderboard();
      setBoard(r.leaderboard);
      setBoardError("");
    } catch (e: any) {
      setBoardError(errorMessage(e, t, "coach.rowing.errBoard"));
    }
  }, [t]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  async function save() {
    setError("");
    setResult(null);
    // Ник в приоритете; если он пуст — берём телефон.
    let target: { phone?: string; nickname?: string };
    if (nick.trim()) {
      target = { nickname: nick.trim() };
    } else if (phone.trim()) {
      const p = normalizePhone(phone);
      if (!p) return setError(t("coach.rowing.errPhone"));
      target = { phone: p };
    } else {
      return setError(t("coach.rowing.errWho"));
    }
    const d = Number(distance);
    if (!Number.isFinite(d) || d <= 0) return setError(t("coach.rowing.errDistance"));
    setSaving(true);
    try {
      const r = await coachRowing(target, d);
      setResult(r);
      // Очищаем форму: у станции сразу вносят следующего участника.
      setNick("");
      setPhone("");
      setDistance("");
      await loadBoard();
    } catch (e: any) {
      // Прод-бэкенд может ещё не уметь запись по нику («phone required») —
      // подсказываем тренеру ввести телефон, а не показываем общую ошибку.
      if (target.nickname && e?.code && /phone/i.test(String(e.code)))
        return setError(t("coach.rowing.errNickUnsupported"));
      setError(errorMessage(e, t, "coach.rowing.errSave"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-8 max-w-md">
      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-extrabold">{t("coach.rowing.recordTitle")}</h2>
        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">{t("coach.rowing.nickLabel")}</span>
          <input
            value={nick}
            onChange={(e) => setNick(e.target.value)}
            placeholder={t("coach.rowing.nickPlaceholder")}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="w-full bg-input text-black rounded-[14px] px-4 py-3 text-base placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">{t("coach.rowing.orPhoneLabel")}</span>
          <PhoneInput value={phone} onChange={setPhone} />
        </label>
        <p className="text-muted text-xs -mt-1">{t("coach.rowing.editHint")}</p>
        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">{t("coach.rowing.distanceLabel")}</span>
          <input
            type="number"
            inputMode="numeric"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
            placeholder={t("coach.rowing.distancePlaceholder")}
            className="w-full bg-input text-black rounded-[14px] px-4 py-3 text-base placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
          />
        </label>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="w-full h-14 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity disabled:opacity-60 shadow-violet-btn"
        >
          {saving ? t("coach.rowing.saving") : t("coach.rowing.save")}
        </button>
        {result && (
          <div className="bg-card border border-line-2 rounded-[18px] p-4 flex items-center gap-6">
            <div>
              <div className="text-muted text-xs">{t("coach.rowing.distance")}</div>
              <div className="text-xl font-bold mt-0.5">{result.distance_m} {t("unit.meters")}</div>
            </div>
            <div>
              <div className="text-muted text-xs">{t("coach.rowing.power")}</div>
              <div className="text-xl font-bold mt-0.5">{result.power_w} {t("unit.watts")}</div>
            </div>
          </div>
        )}
      </section>

      {/* Последние внесённые — тренер сразу видит, что записалось */}
      {(() => {
        const recent = board
          .filter((r) => r.recorded_at)
          .sort((a, b) => new Date(b.recorded_at!).getTime() - new Date(a.recorded_at!).getTime())
          .slice(0, 5);
        if (recent.length === 0) return null;
        return (
          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-extrabold">{t("coach.rowing.recent")}</h2>
            <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
              {recent.map((row, i) => (
                <button
                  key={`${row.nickname}-${i}`}
                  type="button"
                  onClick={() => {
                    if (!row.nickname) return;
                    setNick(row.nickname);
                    setPhone("");
                    setDistance(String(row.distance_m));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="w-full text-left flex items-center gap-3 p-3.5 active:bg-white/[0.04] transition-colors"
                >
                  <span className="text-muted text-xs w-12 shrink-0">
                    {new Date(row.recorded_at!).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                  </span>
                  <span className="flex-1 text-sm truncate">{row.nickname || t("coach.rowing.guestFallback")}</span>
                  <span className="text-sm font-semibold">{row.distance_m} {t("unit.meters")}</span>
                  <span className="text-muted text-sm w-16 text-right">{Math.round(row.power_w)} {t("unit.watts")}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0 opacity-50">
                    <path d="M4 20h4L19 9l-4-4L4 16v4z" stroke="#9E84FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ))}
            </div>
          </section>
        );
      })()}

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-extrabold">{t("coach.rowing.leaderboard")}</h2>
            <RefreshButton onRefresh={loadBoard} label={t("admin.refresh")} />
          </div>
          <div className="flex gap-1.5">
            {(["F", "M"] as const).map((sx) => (
              <button
                key={sx}
                type="button"
                onClick={() => setBoardSex(sx)}
                className={`h-9 px-3.5 rounded-full text-[13px] font-bold transition-colors ${
                  boardSex === sx
                    ? "bg-violet-grad text-white shadow-violet"
                    : "bg-white/[0.06] text-white/70 active:bg-white/10"
                }`}
              >
                {sx === "F" ? t("prizes.rowing.women") : t("prizes.rowing.men")}
              </button>
            ))}
          </div>
        </div>
        {boardError && <p className="text-sm text-red-400">{boardError}</p>}
        <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
          {board.filter((row) => row.sex === boardSex).map((row, i) => (
            <div key={`${row.nickname}-${i}`} className="flex items-center gap-3 p-4">
              <span className="w-7 shrink-0 text-muted text-sm font-semibold">{i + 1}</span>
              <span className="flex-1 text-sm">{row.nickname || t("coach.rowing.guestFallback")}</span>
              <span className="text-sm font-semibold">{row.distance_m} {t("unit.meters")}</span>
              <span className="text-muted text-sm w-16 text-right">{Math.round(row.power_w)} {t("unit.watts")}</span>
            </div>
          ))}
          {board.filter((row) => row.sex === boardSex).length === 0 && (
            <div className="p-4 text-muted text-sm">{t("coach.rowing.boardEmpty")}</div>
          )}
        </div>
      </section>
    </div>
  );
}

export default function Coach() {
  const { t } = useT();
  const role = useStaffRole();
  const canRow = role === "coach" || role === "admin";
  // Маркетинг проверяет личные сторис-заявки прямо на стенде.
  const canStories = role === "marketing" || role === "admin";
  // Каждая роль открывает свою основную вкладку: тренер вносит греблю,
  // маркетинг проверяет сторис; RedBull и админ начинают с кланов.
  const [tab, setTab] = useState<"clans" | "rowing" | "stories" | "board">(() =>
    role === "coach" ? "rowing" : role === "marketing" ? "stories" : "clans",
  );

  // RedBull награждает победителей гребли — им нужен лидерборд (только чтение).
  const canBoard = role === "redbull";
  const tabs: { key: "clans" | "rowing" | "stories" | "board"; labelKey: string }[] = [
    { key: "clans", labelKey: "coach.tab.clans" },
    ...(canRow ? [{ key: "rowing", labelKey: "coach.tab.rowing" } as const] : []),
    ...(canBoard ? [{ key: "board", labelKey: "coach.rowing.leaderboard" } as const] : []),
    ...(canStories ? [{ key: "stories", labelKey: "admin.tab.stories" } as const] : []),
  ];

  return (
    <div className="min-h-dvh px-6 py-8 max-w-5xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl font-extrabold tracking-tight">
            {t(role ? `coach.title.${role}` : "coach.title")}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <LangPills />
            {role && (
              <span className="text-xs font-semibold text-violet-lighter bg-violet-t12 rounded-full px-3 py-1.5">
                {t("staff.youAre", { role: t(`staff.role.${role}`) })}
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                clearStaffCode();
                location.reload(); // StaffGate снова спросит код
              }}
              className="h-8 px-3 rounded-full bg-white/[0.06] text-white/70 text-xs font-semibold ring-1 ring-white/10 active:bg-white/10 transition-colors"
            >
              {t("profile.logout")}
            </button>
          </div>
        </div>
        {tabs.length > 1 && (
          <div className="mt-4 flex gap-2">
            {tabs.map(({ key, labelKey }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`h-11 px-5 rounded-[14px] font-bold text-sm transition-colors ${
                  tab === key ? "bg-violet-grad text-white shadow-violet" : "bg-white/[0.06] text-white active:bg-white/10"
                }`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        )}
      </header>

      {canRow && tab === "rowing" ? (
        <RowingTab />
      ) : canBoard && tab === "board" ? (
        <BoardTab />
      ) : canStories && tab === "stories" ? (
        <StoryReview />
      ) : (
        <ClansTab role={role} />
      )}
    </div>
  );
}
